import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import StorefrontCarousel from "../shared/StorefrontCarousel";
import ProductSearchOverlay from "../shared/ProductSearchOverlay";
import {
  getDefaultNavItems,
  getBuyerSegments,
  getServiceFeatures,
  getQualityControlPoints,
  normalizeNavTree,
  collectNavNodes,
  matchesNavPath,
  getNavItemId,
  getBreadcrumbItems
} from "./storefrontData";
import {
  getSeoQuickLinks,
  getSeoScenarios,
  getSeoProcessSteps,
  getBlogHighlights,
  getSeoFaqFallbackItems,
  getSeoKeywordBody
} from "./homeSeoContent";
import { renderStorefrontIcon } from "./storefrontIcons";
import { STOREFRONT_SUPPORTED_LOCALES, t } from "../../i18n";

const ADMIN_AUTH_UPDATED_EVENT = "ft-admin-auth-updated";
const USER_AUTH_UPDATED_EVENT = "ft-user-auth-updated";
const PRIVACY_MODE_STORAGE_KEY = "ft_privacy_mode";
const SEARCH_HISTORY_KEY = "ft_search_history";
const PRIVACY_MODE_ENHANCED = "enhanced";
const PRIVACY_MODE_BASIC = "basic";
const PRIVACY_SETTINGS_OPEN_EVENT = "ft-open-privacy-settings";
const BEST_SELLING_PRODUCTS_LIMIT = 6;
const FEATURED_PRODUCTS_LIMIT = 5;
const CATEGORY_SHOWCASE_LIMIT = 5;
const CATEGORY_SHOWCASE_PRIORITY = [
  "Yoga Products",
  "Pilates Products",
  "Home Fitness Equipment",
  "Foam Rollers & Recovery",
  "Specialty Accessories",
  "Specialty/Accessories",
  "Yoga Mats",
  "Resistance Bands"
];
 
const FALLBACK_ART = "/assets/fallback-art.jpg";

const PARTNER_LOGOS = [
  { name: "Partner 1", src: "/assets/partners/partner1.svg" },
  { name: "Partner 2", src: "/assets/partners/partner2.svg" },
  { name: "Partner 3", src: "/assets/partners/partner3.svg" },
  { name: "Partner 4", src: "/assets/partners/partner4.svg" }
];

const FACTORY_STATS = [
  { key: "since", labelKey: "factoryStatsSince", icon: "🏢" },
  { key: "capacity", labelKey: "factoryStatsCapacity", icon: "⚙️" },
  { key: "export", labelKey: "factoryStatsExport", icon: "🌍" },
  { key: "certification", labelKey: "factoryStatsCert", icon: "🏆" }
];

const BUYER_REVIEWS = [
  {
    key: "review1",
    nameKey: "reviewName1",
    roleKey: "reviewRole1",
    countryKey: "reviewCountry1",
    countryCode: "us",
    textKey: "reviewText1",
    rating: 5,
    avatar: "/assets/customer/serah.webp",
    avatarFallback: "/assets/customer/serah.webp",
    avatarPosition: "center 22%"
  },
  {
    key: "review2",
    nameKey: "reviewName2",
    roleKey: "reviewRole2",
    countryKey: "reviewCountry2",
    countryCode: "sa",
    textKey: "reviewText2",
    rating: 5,
    avatar: "/assets/customer/omar.jpeg",
    avatarFallback: "/assets/customer/omar.jpeg",
    avatarPosition: "center 20%"
  },
  {
    key: "review3",
    nameKey: "reviewName3",
    roleKey: "reviewRole3",
    countryKey: "reviewCountry3",
    countryCode: "fr",
    textKey: "reviewText3",
    rating: 5,
    avatar: "/assets/customer/sophie.webp",
    avatarFallback: "/assets/customer/sophie.webp",
    avatarPosition: "center 16%"
  },
  {
    key: "review4",
    nameKey: "reviewName4",
    roleKey: "reviewRole4",
    countryKey: "reviewCountry4",
    countryCode: "gb",
    textKey: "reviewText4",
    rating: 5,
    avatar: "/assets/customer/james.webp",
    avatarFallback: "/assets/customer/james.webp",
    avatarPosition: "center 20%"
  }
];

const TRUST_HIGHLIGHTS = [
  { key: "suppliers", iconImage: "/assets/feature-icons/verified.svg", labelKey: "trustA" },
  { key: "private-label", iconImage: "/assets/feature-icons/private-label.svg", labelKey: "trustB" },
  { key: "lead-time", iconImage: "/assets/feature-icons/moq.svg", labelKey: "trustC" },
  { key: "inspection", iconImage: "/assets/feature-icons/inspection.svg", labelKey: "trustD" }
];

const HOME_MODULE_KEYS = [
  "intro",
  "quick-nav",
  "categories",
  "best-selling",
  "featured",
  "solutions",
  "scenarios",
  "story",
  "process",
  "inquiry"
];

const HOMEPAGE_SHOWCASE_IMAGES = [
  {
    key: "expo-booth-main",
    src: "/assets/about/1.png",
    fallback: "/assets/solutions/scenario-studio-starter.jpg",
    altKey: "homeShowcaseAlt1"
  },
  {
    key: "expo-mat-wall",
    src: "/assets/about/2.png",
    fallback: "/assets/solutions/scenario-retail-display.jpg",
    altKey: "homeShowcaseAlt2"
  },
  {
    key: "expo-accessories",
    src: "/assets/about/3.png",
    fallback: "/assets/solutions/scenario-online-bundle.jpg",
    altKey: "homeShowcaseAlt3"
  }
];

function dismissNavFocus() {
  if (typeof document === "undefined") {
    return;
  }

  const activeElement = document.activeElement;
  if (activeElement && typeof activeElement.blur === "function") {
    activeElement.blur();
  }
}

function buildGroupedNavItems(items) {
  if (!Array.isArray(items) || !items.length) {
    return [];
  }

  return items;
}

function handleBookmarkSite() {
  if (typeof window === "undefined") {
    return;
  }

  const title = document.title || "LOYA SPORT";
  const url = window.location.origin || window.location.href;

  try {
    if (window.sidebar && typeof window.sidebar.addPanel === "function") {
      window.sidebar.addPanel(title, url, "");
      return;
    }

    if (window.external && typeof window.external.AddFavorite === "function") {
      window.external.AddFavorite(url, title);
      return;
    }
  } catch (_err) {
    // Ignore and fallback to manual instruction.
  }

  window.alert(t("bookmarkHint"));
}

function formatPrice(value, currency) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 2
  }).format(value);
}

function flattenNavTree(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.flatMap((item) => [item, ...flattenNavTree(item?.children)]);
}

function parseProductGroupId(product) {
  const sourceGroupId = Number(product?.sourceGroupId || 0);
  if (Number.isFinite(sourceGroupId) && sourceGroupId > 0) {
    return sourceGroupId;
  }

  const rawCategoryPath = String(product?.categoryPath || "").trim();
  if (/^\d+$/.test(rawCategoryPath)) {
    return Number(rawCategoryPath);
  }

  return 0;
}

function parseGroupIdFromPath(path) {
  const rawPath = String(path || "").trim();
  if (!rawPath || !rawPath.startsWith("/products")) {
    return 0;
  }

  const queryString = rawPath.includes("?") ? rawPath.split("?")[1] : "";
  const params = new URLSearchParams(queryString || "");
  const groupId = Number(params.get("groupId") || params.get("categoryId") || 0);
  return Number.isFinite(groupId) && groupId > 0 ? groupId : 0;
}

function normalizeCategoryPriorityName(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "").trim();
}

function getCategoryShowcaseRank(title) {
  const normalizedTitle = normalizeCategoryPriorityName(title);
  const rank = CATEGORY_SHOWCASE_PRIORITY.findIndex((item) => normalizeCategoryPriorityName(item) === normalizedTitle);
  return rank >= 0 ? rank : CATEGORY_SHOWCASE_PRIORITY.length;
}

function getFeaturedCategoryTitle(item) {
  const rawCategory = String(item?.category || "").trim();
  const fallback = "Yoga Series";
  const source = rawCategory || fallback;

  const compact = source
    .replace(/\b(Products?|Equipment|Accessories|Supplies|Collection|Series)\b/gi, "")
    .replace(/[|,/]|\s+-\s+|\s+–\s+|\s+·\s+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

  const normalized = compact || source;
  const words = normalized.split(/\s+/).filter(Boolean);
  if (!words.length) {
    return fallback;
  }

  return words.slice(0, 3).join(" ");
}

function renderCountryFlagIcon(countryCode) {
  const code = String(countryCode || "").toLowerCase();

  switch (code) {
    case "us":
      return (
        <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
          <rect width="28" height="20" rx="3" fill="#ffffff" />
          <rect y="0" width="28" height="2" fill="#b22234" />
          <rect y="4" width="28" height="2" fill="#b22234" />
          <rect y="8" width="28" height="2" fill="#b22234" />
          <rect y="12" width="28" height="2" fill="#b22234" />
          <rect y="16" width="28" height="2" fill="#b22234" />
          <rect width="12" height="10" rx="1" fill="#3c3b6e" />
        </svg>
      );
    case "sa":
      return (
        <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
          <rect width="28" height="20" rx="3" fill="#006c35" />
          <rect x="6" y="14" width="16" height="1.6" rx="0.8" fill="#ffffff" />
          <circle cx="20.8" cy="14.8" r="1" fill="#ffffff" />
        </svg>
      );
    case "fr":
      return (
        <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
          <rect width="28" height="20" rx="3" fill="#ffffff" />
          <rect width="9.34" height="20" rx="3" fill="#0055a4" />
          <rect x="18.66" width="9.34" height="20" rx="3" fill="#ef4135" />
        </svg>
      );
    case "gb":
      return (
        <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
          <rect width="28" height="20" rx="3" fill="#012169" />
          <path d="M0 0 L12 0 L28 12 L28 20 L16 20 L0 8 Z" fill="#ffffff" opacity="0.95" />
          <path d="M28 0 L16 0 L0 12 L0 20 L12 20 L28 8 Z" fill="#ffffff" opacity="0.95" />
          <path d="M0 0 L2.4 0 L28 17.6 L28 20 L25.6 20 L0 2.4 Z" fill="#c8102e" />
          <path d="M28 0 L25.6 0 L0 17.6 L0 20 L2.4 20 L28 2.4 Z" fill="#c8102e" />
          <rect x="11" width="6" height="20" fill="#ffffff" />
          <rect y="7" width="28" height="6" fill="#ffffff" />
          <rect x="12" width="4" height="20" fill="#c8102e" />
          <rect y="8" width="28" height="4" fill="#c8102e" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 28 20" role="img" aria-hidden="true">
          <rect width="28" height="20" rx="3" fill="#dde4ef" />
        </svg>
      );
  }
}

function readPrivacyMode() {
  if (typeof window === "undefined") {
    return "";
  }

  const value = String(window.localStorage.getItem(PRIVACY_MODE_STORAGE_KEY) || "").trim().toLowerCase();
  if (value === PRIVACY_MODE_ENHANCED || value === PRIVACY_MODE_BASIC) {
    return value;
  }

  return "";
}

function writePrivacyMode(value) {
  if (typeof window === "undefined") {
    return;
  }

  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    window.localStorage.removeItem(PRIVACY_MODE_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(PRIVACY_MODE_STORAGE_KEY, normalized);
}

function renderWorkflowStepIcon(index) {
  switch (index) {
    case 0:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      );
    case 1:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 6h18"></path>
          <path d="M3 12h12"></path>
          <path d="M3 18h10"></path>
        </svg>
      );
    case 2:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m3 7 9-4 9 4-9 4-9-4Z"></path>
          <path d="m3 7 0 10 9 4 9-4V7"></path>
        </svg>
      );
    case 3:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 12h13"></path>
          <path d="M13 5l7 7-7 7"></path>
        </svg>
      );
    case 4:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20 6 9 17l-5-5"></path>
        </svg>
      );
    case 5:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9"></circle>
          <path d="M12 7v5l3 3"></path>
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 12h13"></path>
          <path d="M13 5l7 7-7 7"></path>
          <path d="M3 5v14"></path>
        </svg>
      );
  }
}

export default function StorefrontPage({ apiBaseUrl, storefrontLocale = "en", pageTemplateConfig = {}, themeLayoutConfig = {} }) {
  const location = useLocation();
  const resolvedStorefrontLocale = STOREFRONT_SUPPORTED_LOCALES.has(String(storefrontLocale || "").trim().toLowerCase())
    ? String(storefrontLocale || "").trim().toLowerCase()
    : "en";
  const defaultNavItems = useMemo(() => getDefaultNavItems(), [resolvedStorefrontLocale]);
  const seoQuickLinks = useMemo(() => getSeoQuickLinks(), [resolvedStorefrontLocale]);
  const seoScenarios = useMemo(() => getSeoScenarios(), [resolvedStorefrontLocale]);
  const seoProcessSteps = useMemo(() => getSeoProcessSteps(), [resolvedStorefrontLocale]);
  const blogHighlights = useMemo(() => getBlogHighlights(), [resolvedStorefrontLocale]);
  const seoFaqFallbackItems = useMemo(() => getSeoFaqFallbackItems(), [resolvedStorefrontLocale]);
  const seoKeywordBody = useMemo(() => getSeoKeywordBody(), [resolvedStorefrontLocale]);
  const buyerSegments = useMemo(() => getBuyerSegments(), [resolvedStorefrontLocale]);
  const serviceFeatures = useMemo(() => getServiceFeatures(), [resolvedStorefrontLocale]);
  const qualityControlPoints = useMemo(() => getQualityControlPoints(), [resolvedStorefrontLocale]);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return Boolean(localStorage.getItem("ft_admin_token"));
  });
  const [currency, setCurrency] = useState("USD");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPricing, setShowPricing] = useState(false);
  const [storefrontTitle, setStorefrontTitle] = useState("LOYA SPORT");
  const [navItems, setNavItems] = useState(() => defaultNavItems);
  const [hasCustomNavMenu, setHasCustomNavMenu] = useState(false);
  const [activeCategory, setActiveCategory] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [seoMetaContext, setSeoMetaContext] = useState({
    title: "",
    description: "",
    canonicalUrl: "",
    ogImage: ""
  });
  const [privacyMode, setPrivacyMode] = useState(() => readPrivacyMode());
  const [privacyPromptForcedOpen, setPrivacyPromptForcedOpen] = useState(false);
  const [faqKnowledgeItems, setFaqKnowledgeItems] = useState(() => seoFaqFallbackItems);

  const resolvedCanonicalUrl = useMemo(() => {
    if (seoMetaContext.canonicalUrl) {
      return seoMetaContext.canonicalUrl;
    }

    if (typeof window !== "undefined") {
      return `${window.location.origin}/`;
    }

    return "";
  }, [seoMetaContext.canonicalUrl]);

  const homepageStructuredData = useMemo(() => {
    const organization = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: storefrontTitle || "LOYA SPORT",
      url: resolvedCanonicalUrl || undefined,
      logo: "/assets/about/logo.png"
    };

    const website = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: storefrontTitle || "LOYA SPORT",
      url: resolvedCanonicalUrl || undefined,
      inLanguage: resolvedStorefrontLocale || "en"
    };

    const faqPage = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqKnowledgeItems.slice(0, 8).map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer
        }
      }))
    };

    return { organization, website, faqPage };
  }, [faqKnowledgeItems, resolvedCanonicalUrl, resolvedStorefrontLocale, storefrontTitle]);

  useEffect(() => {
    function syncAuthState() {
      setIsAuthenticated(Boolean(localStorage.getItem("ft_admin_token")));
    }

    window.addEventListener(ADMIN_AUTH_UPDATED_EVENT, syncAuthState);
    window.addEventListener(USER_AUTH_UPDATED_EVENT, syncAuthState);
    window.addEventListener("storage", syncAuthState);

    return () => {
      window.removeEventListener(ADMIN_AUTH_UPDATED_EVENT, syncAuthState);
      window.removeEventListener(USER_AUTH_UPDATED_EVENT, syncAuthState);
      window.removeEventListener("storage", syncAuthState);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadFaqKnowledgeItems() {
      if (!apiBaseUrl) {
        return;
      }

      try {
        const response = await fetch(`${apiBaseUrl}/company/faq/list?isActive=true&limit=8`, {
          cache: "no-store"
        });

        if (!response.ok) {
          throw new Error(`FAQ request failed: ${response.status}`);
        }

        const data = await response.json();
        if (cancelled) {
          return;
        }

        const normalizedItems = Array.isArray(data?.faqKnowledge)
          ? data.faqKnowledge
              .map((item, index) => ({
                id: String(item?.id || `faq-${index + 1}`),
                question: String(item?.question || "").trim(),
                answer: String(item?.answer || "").trim()
              }))
              .filter((item) => item.question && item.answer)
          : [];

        if (normalizedItems.length) {
          const merged = [...normalizedItems];
          seoFaqFallbackItems.forEach((fallbackItem) => {
            if (merged.length >= 8) {
              return;
            }

            const exists = merged.some(
              (item) => item.question.toLowerCase() === fallbackItem.question.toLowerCase()
            );
            if (!exists) {
              merged.push(fallbackItem);
            }
          });

          setFaqKnowledgeItems(merged.slice(0, 8));
        }
      } catch (_err) {
        // Keep fallback FAQ list when backend FAQ API is unavailable.
        if (!cancelled) {
          setFaqKnowledgeItems(seoFaqFallbackItems);
        }
      }
    }

    loadFaqKnowledgeItems();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, seoFaqFallbackItems]);

  // 统一用 t(key) 获取文案，无需 texts 变量

  useEffect(() => {
    let cancelled = false;

    async function loadConfig() {
      try {
        const response = await fetch(`${apiBaseUrl}/storefront/config`);
        if (!response.ok) {
          throw new Error("Failed to load storefront config");
        }

        const data = await response.json();
        if (cancelled) {
          return;
        }

        if (Array.isArray(data.navMenu) && data.navMenu.length) {
          setHasCustomNavMenu(true);
          setNavItems(normalizeNavTree(data.navMenu));
        } else {
          setHasCustomNavMenu(false);
          setNavItems(defaultNavItems);
        }

        const settings = data.storeSettings && typeof data.storeSettings === "object"
          ? data.storeSettings
          : {};

        const title = String(settings.storefrontTitle || "").trim() || "LOYA SPORT";
        const seoTitle = String(settings.seo?.metaTitle || "").trim() ||
          t("homeSeoFallbackTitle");
        const seoDescription = String(settings.seo?.metaDescription || "").trim() ||
          t("homeSeoFallbackDescription");
        const seoKeywords = String(settings.seo?.keywords || "").trim() ||
          "product sourcing, product customization, OEM ODM support, private label support, sample order support, wholesale supply";
        const seoCanonicalUrl = String(settings.seo?.canonicalUrl || "").trim() ||
          (typeof window !== "undefined" ? `${window.location.origin}/` : "");
        const seoRobots = String(settings.seo?.robots || "").trim() || "index,follow";
        const ogTitle = String(settings.seo?.ogTitle || "").trim() || seoTitle;
        const ogDescription = String(settings.seo?.ogDescription || "").trim() || seoDescription;
        const ogImage = String(settings.seo?.ogImage || "").trim();
        const twitterCard = String(settings.seo?.twitterCard || "").trim() || "summary_large_image";
        const twitterSite = String(settings.seo?.twitterSite || "").trim();

        setStorefrontTitle(title);
        setSeoMetaContext({
          title: seoTitle,
          description: seoDescription,
          canonicalUrl: seoCanonicalUrl,
          ogImage
        });

        const ensureMetaTag = (selector, attr, content) => {
          if (!content) {
            return;
          }

          let element = document.querySelector(selector);
          if (!element) {
            element = document.createElement("meta");
            element.setAttribute(attr, selector.match(/\"(.+?)\"/)?.[1] || "");
            document.head.appendChild(element);
          }
          element.setAttribute("content", content);
        };

        let descriptionMeta = document.querySelector('meta[name="description"]');
        if (!descriptionMeta) {
          descriptionMeta = document.createElement("meta");
          descriptionMeta.setAttribute("name", "description");
          document.head.appendChild(descriptionMeta);
        }
        descriptionMeta.setAttribute("content", seoDescription);

        let keywordsMeta = document.querySelector('meta[name="keywords"]');
        if (!keywordsMeta) {
          keywordsMeta = document.createElement("meta");
          keywordsMeta.setAttribute("name", "keywords");
          document.head.appendChild(keywordsMeta);
        }
        keywordsMeta.setAttribute("content", seoKeywords);

        if (seoCanonicalUrl) {
          let canonical = document.querySelector('link[rel="canonical"]');
          if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
          }
          canonical.setAttribute("href", seoCanonicalUrl);
        }

        ensureMetaTag('meta[name="robots"]', "name", seoRobots);
        ensureMetaTag('meta[property="og:title"]', "property", ogTitle);
        ensureMetaTag('meta[property="og:description"]', "property", ogDescription);
        ensureMetaTag('meta[property="og:type"]', "property", "website");
        ensureMetaTag('meta[property="og:url"]', "property", seoCanonicalUrl);
        ensureMetaTag('meta[property="og:image"]', "property", ogImage);
        ensureMetaTag('meta[name="twitter:card"]', "name", twitterCard);
        ensureMetaTag('meta[name="twitter:title"]', "name", ogTitle);
        ensureMetaTag('meta[name="twitter:description"]', "name", ogDescription);
        ensureMetaTag('meta[name="twitter:image"]', "name", ogImage);
        ensureMetaTag('meta[name="twitter:site"]', "name", twitterSite);
      } catch (_err) {
        if (!cancelled) {
          setHasCustomNavMenu(false);
          setNavItems(defaultNavItems);
          setStorefrontTitle("LOYA SPORT");
          setSeoMetaContext({
            title: t("homeSeoFallbackTitle"),
            description: t("homeSeoFallbackDescription"),
            canonicalUrl: typeof window !== "undefined" ? `${window.location.origin}/` : "",
            ogImage: ""
          });
        }
      }
    }

    async function loadProducts() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${apiBaseUrl}/products?lang=${resolvedStorefrontLocale}&currency=${currency}`);

        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }

        const data = await response.json();

        if (!cancelled) {
          const usesFallbackCatalog = String(data?.meta?.source || "").trim().toLowerCase() === "fallback";
          const nextProducts = usesFallbackCatalog ? [] : (data.products || []);
          setProducts(nextProducts);
          setShowPricing(false);
          if (nextProducts.length && !activeCategory) {
            setActiveCategory(nextProducts[0].category || "");
          }
        }
      } catch (_err) {
        if (!cancelled) {
          setProducts([]);
          setShowPricing(false);
          setError(t("error"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadConfig();
    loadProducts();

    return () => {
      cancelled = true;
    };
  }, [activeCategory, apiBaseUrl, currency, defaultNavItems, resolvedStorefrontLocale]);

  useEffect(() => {
    if (!hasCustomNavMenu) {
      setNavItems(defaultNavItems);
    }
  }, [defaultNavItems, hasCustomNavMenu]);

  const categories = useMemo(() => {
    const grouped = new Map();
    products.forEach((item) => {
      if (!grouped.has(item.category)) {
        grouped.set(item.category, {
          name: item.category,
          imageUrl: item.imageUrl,
          count: 0
        });
      }
      grouped.get(item.category).count += 1;
    });

    return Array.from(grouped.values()).slice(0, 6);
  }, [products]);

  useEffect(() => {
    if (categories.length && !categories.some((item) => item.name === activeCategory)) {
      setActiveCategory(categories[0].name);
    }
  }, [activeCategory, categories]);

  useEffect(() => {
    if (location.pathname !== "/" || !location.hash) {
      return;
    }

    const targetId = location.hash.replace("#", "");
    if (!targetId) {
      return;
    }

    const timer = window.setTimeout(() => {
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 60);

    return () => {
      window.clearTimeout(timer);
    };
  }, [location.hash, location.pathname]);

  const productGroupPathById = useMemo(() => {
    const flatItems = flattenNavTree(navItems);
    const productsItem = flatItems.find((item) => String(item?.key || "").trim().toLowerCase() === "products");
    const descendants = flattenNavTree(productsItem?.children || []);
    const mapping = new Map();

    descendants.forEach((item) => {
      const groupId = parseGroupIdFromPath(item?.path);
      if (!groupId || mapping.has(groupId)) {
        return;
      }

      mapping.set(groupId, String(item?.path || "").trim());
    });

    return mapping;
  }, [navItems]);

  const categoryShowcaseItems = useMemo(() => {
    const grouped = new Map();

    function isOthersCategory(value) {
      const normalized = String(value || "").trim().toLowerCase();
      return normalized === "other" || normalized === "others" || normalized === "其他";
    }

    products.forEach((item) => {
      const categoryName = String(item?.category || "Others").trim() || "Others";
      if (isOthersCategory(categoryName)) {
        return;
      }

      const entry = grouped.get(categoryName) || {
        key: categoryName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "others",
        title: categoryName,
        imageUrl: "",
        coverProductId: 0,
        count: 0,
        groupId: 0
      };

      const groupId = parseProductGroupId(item);

      if (!entry.imageUrl) {
        entry.imageUrl = item?.imageUrl || item?.mainImages?.[0] || "";
        entry.coverProductId = Number(item?.id || 0);
      }

      if (!entry.groupId && groupId) {
        entry.groupId = groupId;
      }

      entry.count += 1;
      grouped.set(categoryName, entry);
    });

    const showcaseItems = Array.from(grouped.values())
      .sort((a, b) => {
        const rankDiff = getCategoryShowcaseRank(a.title) - getCategoryShowcaseRank(b.title);
        if (rankDiff !== 0) {
          return rankDiff;
        }

        return String(a.title || "").localeCompare(String(b.title || ""));
      })
      .slice(0, CATEGORY_SHOWCASE_LIMIT)
      .map((item) => ({
        ...item,
        path: item.groupId
          ? (productGroupPathById.get(item.groupId) || `/products?groupId=${item.groupId}&group=${encodeURIComponent(item.title)}`)
          : `/products?group=${encodeURIComponent(item.title)}`
      }));

    if (showcaseItems.length >= CATEGORY_SHOWCASE_LIMIT) {
      return showcaseItems;
    }

    const usedProductIds = new Set(
      showcaseItems
        .map((item) => Number(item?.coverProductId || 0))
        .filter((id) => Number.isFinite(id) && id > 0)
    );

    products.forEach((item, index) => {
      if (showcaseItems.length >= CATEGORY_SHOWCASE_LIMIT) {
        return;
      }

      const productId = Number(item?.id || 0);
      if (productId > 0 && usedProductIds.has(productId)) {
        return;
      }

      const categoryName = String(item?.category || "Others").trim() || "Others";
      if (isOthersCategory(categoryName)) {
        return;
      }

      const groupId = parseProductGroupId(item);
      const title = String(item?.name || "").trim() || categoryName || `Product ${index + 1}`;

      showcaseItems.push({
        key: `product-${productId > 0 ? productId : index}`,
        title,
        imageUrl: item?.imageUrl || item?.mainImages?.[0] || "",
        coverProductId: productId > 0 ? productId : 0,
        count: 1,
        groupId,
        path: productId > 0
          ? `/products/${productId}`
          : (groupId
            ? (productGroupPathById.get(groupId) || `/products?groupId=${groupId}&group=${encodeURIComponent(categoryName)}`)
            : `/products?group=${encodeURIComponent(categoryName)}`)
      });

      if (productId > 0) {
        usedProductIds.add(productId);
      }
    });

    return showcaseItems;
  }, [productGroupPathById, products]);

  const featuredProducts = useMemo(() => {
    // Featured section now strictly displays admin-recommended products.
    return products
      .filter((item) => item?.isRecommended === true)
      .slice(0, FEATURED_PRODUCTS_LIMIT);
  }, [products]);

  const bestSellingProducts = useMemo(() => {
    function normalizeTimestamp(value) {
      const rawValue = String(value || "").trim();
      if (!rawValue) {
        return 0;
      }

      if (/^\d+$/.test(rawValue)) {
        return Number(rawValue);
      }

      const parsed = Date.parse(rawValue);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    const rankedProducts = products
      .map((item) => {
        const numericSalesCount = Number(item?.salesCount || 0);

        return {
          item,
          id: Number(item?.id || 0),
          isBestSelling: item?.isBestSelling === true,
          salesCount: Number.isFinite(numericSalesCount) && numericSalesCount > 0 ? numericSalesCount : 0,
          updatedAtMs: normalizeTimestamp(item?.updatedAt || item?.createdAt)
        };
      });

    const hasManualBestSelling = rankedProducts.some((entry) => entry.isBestSelling);

    rankedProducts.sort((left, right) => {
      if (hasManualBestSelling && left.isBestSelling !== right.isBestSelling) {
        return left.isBestSelling ? -1 : 1;
      }

      if (!hasManualBestSelling && left.salesCount !== right.salesCount) {
        return right.salesCount - left.salesCount;
      }

      if (left.updatedAtMs !== right.updatedAtMs) {
        return right.updatedAtMs - left.updatedAtMs;
      }

      if (hasManualBestSelling && left.salesCount !== right.salesCount) {
        return right.salesCount - left.salesCount;
      }

      return right.id - left.id;
    });

    return rankedProducts.slice(0, BEST_SELLING_PRODUCTS_LIMIT).map(({ item }) => ({
      id: item.id,
      name: item.name,
      category: item.category || "Others",
      path: `/products/${item.id}`,
      imageUrl: item.imageUrl || item.mainImages?.[0] || ""
    }));
  }, [products]);

  function renderEmptyProductsState(sectionLabel) {
    return (
      <article className="loya-empty-state" role="status" aria-live="polite">
          <span className="loya-empty-state-badge">{t("homeEmptyBadge")}</span>
          <h3>{t("homeEmptyTitle")}</h3>
          <p>{`${sectionLabel}${t("homeEmptySuffix")}`}</p>
      </article>
    );
  }

  const groupedNavItems = useMemo(() => buildGroupedNavItems(navItems), [navItems]);
  const activeNavId = useMemo(() => {
    const flatItems = collectNavNodes(groupedNavItems);
    const matchedItem = flatItems.find(({ item }) => matchesNavPath(location, item.path));
    return matchedItem ? getNavItemId(matchedItem.item, matchedItem.index) : "";
  }, [groupedNavItems, location]);

  function isNavItemActive(item, fallbackIndex = "0") {
    if (!item || !activeNavId) {
      return false;
    }

    return getNavItemId(item, fallbackIndex) === activeNavId;
  }

  const breadcrumbItems = useMemo(
    () => getBreadcrumbItems(location.pathname, navItems, t("heroTag")),
    [location.pathname, navItems]
  );
  const shouldShowBreadcrumb = location.pathname !== "/" && !location.pathname.startsWith("/admin");
  const privacyPromptOpen = privacyPromptForcedOpen;
  const privacyEnhanced = privacyMode === PRIVACY_MODE_ENHANCED;
  const homeModuleOrder = useMemo(() => {
    const configuredOrder = Array.isArray(themeLayoutConfig?.home?.moduleOrder)
      ? themeLayoutConfig.home.moduleOrder
      : [];
    const normalized = [];

    configuredOrder.forEach((item) => {
      const key = String(item || "").trim();
      if (HOME_MODULE_KEYS.includes(key) && !normalized.includes(key)) {
        normalized.push(key);
      }
    });

    HOME_MODULE_KEYS.forEach((key) => {
      if (!normalized.includes(key)) {
        normalized.push(key);
      }
    });

    return normalized;
  }, [themeLayoutConfig?.home?.moduleOrder]);
  const homeModuleVisibility = pageTemplateConfig?.moduleVisibility || {};
  const homeModuleConfigs = pageTemplateConfig?.modules || {};

  function isHomeModuleVisible(moduleKey) {
    return homeModuleVisibility?.[moduleKey] !== false;
  }

  function getHomeModuleText(moduleKey, fieldKey, fallbackValue = "") {
    return String(homeModuleConfigs?.[moduleKey]?.[fieldKey] || fallbackValue || "").trim();
  }

  function getHomeModuleImage(moduleKey, fallbackValue = "") {
    return String(homeModuleConfigs?.[moduleKey]?.imageUrl || fallbackValue || "").trim();
  }

  function getHomeModuleStyle(moduleKey) {
    const moduleIndex = homeModuleOrder.indexOf(moduleKey);
    return {
      order: moduleIndex >= 0 ? moduleIndex + 1 : HOME_MODULE_KEYS.length + 1,
      ...(isHomeModuleVisible(moduleKey) ? {} : { display: "none" })
    };
  }

  function handleNavClick(event, path) {
    if (!path || !path.startsWith("/#")) {
      return;
    }

    if (location.pathname !== "/") {
      return;
    }

    const targetId = path.slice(2);
    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }

    event.preventDefault();
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", path);
  }

  function handleSearchProduct() {
    setSearchOpen(true);
  }


  function handlePrivacyModeSelection(nextMode) {
    const normalized = String(nextMode || "").trim().toLowerCase();
    if (normalized !== PRIVACY_MODE_ENHANCED && normalized !== PRIVACY_MODE_BASIC) {
      return;
    }

    setPrivacyMode(normalized);
    writePrivacyMode(normalized);
    setPrivacyPromptForcedOpen(false);

    if (normalized === PRIVACY_MODE_BASIC && typeof window !== "undefined") {
      window.localStorage.removeItem(SEARCH_HISTORY_KEY);
    }
  }

  function handleOpenPrivacySettings() {
    setPrivacyPromptForcedOpen(true);
  }

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    function openPrivacySettings() {
      setPrivacyPromptForcedOpen(true);
    }

    window.addEventListener(PRIVACY_SETTINGS_OPEN_EVENT, openPrivacySettings);
    return () => {
      window.removeEventListener(PRIVACY_SETTINGS_OPEN_EVENT, openPrivacySettings);
    };
  }, []);

  function renderNavItems(items, depth = 1, parentIndex = "") {
    if (!Array.isArray(items) || !items.length) {
      return null;
    }

    return items.filter((item) => item.visible !== false).map((item, index) => {
      const indexPath = parentIndex ? `${parentIndex}-${index}` : String(index);
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      const parentActive =
        isNavItemActive(item, indexPath) ||
        collectNavNodes(item.children, indexPath).some(({ item: child, index: childIndex }) => isNavItemActive(child, childIndex));
      const navKey = item.key || `${item.label}-${item.path}-${indexPath}`;

      if (!hasChildren) {
        return (
          <Link
            key={navKey}
            to={item.path}
            className={parentActive ? "active" : ""}
            onClick={(event) => {
              dismissNavFocus();
              handleNavClick(event, item.path);
            }}
          >
            {item.label}
          </Link>
        );
      }

      return (
        <div key={navKey} className={`commercehub-nav-item has-children nav-depth-${depth}${parentActive ? " active" : ""}`}>
          <Link
            to={item.path}
            className={parentActive ? "active" : ""}
            onClick={(event) => {
              dismissNavFocus();
              handleNavClick(event, item.path);
            }}
          >
            {item.label}
          </Link>
          <span className="commercehub-nav-caret" aria-hidden="true">▾</span>
          <div className="commercehub-submenu" role="menu" aria-label={`${item.label} ${t("navSubmenuSuffix")}`}>
            {renderNavItems(item.children, depth + 1, indexPath)}
          </div>
        </div>
      );
    });
  }


  // 移除本地导航栏，使用全局 StorefrontHeader
  return (
    <>

      {shouldShowBreadcrumb ? (
        <div className="commercehub-breadcrumb-bar">
          <div className="container commercehub-breadcrumb-inner">
            <span className="commercehub-breadcrumb-label">{t("homeBreadcrumbCurrent")}</span>
            <nav className="commercehub-breadcrumb-trail" aria-label={t("ariaBreadcrumbNavigation")}>
              {breadcrumbItems.map((item, index) => {
                const isLast = index === breadcrumbItems.length - 1;

                return (
                  <span
                    key={`${item.label}-${index}`}
                    className={`commercehub-crumb${isLast ? " is-current" : ""}`}
                  >
                    {item.path && !isLast ? <Link to={item.path}>{item.label}</Link> : <span>{item.label}</span>}
                    {!isLast ? <span className="commercehub-crumb-separator">/</span> : null}
                  </span>
                );
              })}
            </nav>
          </div>
        </div>
      ) : null}

      {privacyPromptOpen ? (
        <div className="privacy-consent-backdrop" role="dialog" aria-modal="true" aria-label={t("ariaPrivacyPreference")}>
          <section className="privacy-consent-card">
            <h3>{t("privacyTitle")}</h3>
            <p>{t("privacyBody")}</p>
            <ul>
              <li>{t("privacyOptionEnhanced")}</li>
              <li>{t("privacyOptionBasic")}</li>
            </ul>
            <div className="privacy-consent-actions">
              <button type="button" className="button" onClick={() => handlePrivacyModeSelection(PRIVACY_MODE_ENHANCED)}>
                {t("privacyAccept")}
              </button>
              <button type="button" className="button ghost" onClick={() => handlePrivacyModeSelection(PRIVACY_MODE_BASIC)}>
                {t("privacyReject")}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      <StorefrontCarousel apiBaseUrl={apiBaseUrl} />

      <main className="container page storefront-homepage" style={{ display: "flex", flexDirection: "column" }}>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageStructuredData.organization) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageStructuredData.website) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageStructuredData.faqPage) }}
        />

        <section className="loya-intro-section home-module home-module-intro" aria-label={t("homeAriaIntro") } style={getHomeModuleStyle("intro")}>
          <div className="loya-intro-top">
            <div className="loya-intro-copy">
              <p className="loya-product-kicker">{getHomeModuleText("intro", "subtitle", String(pageTemplateConfig?.heroEyebrow || t("homeBestSellingCardKicker")).trim())}</p>
              <h1>{getHomeModuleText("intro", "title", String(pageTemplateConfig?.heroTitle || t("homeHeroTitle")).trim())}</h1>
              <p>{String(pageTemplateConfig?.heroSubtitle || "").trim()}</p>
              <div className="row-actions" style={{ marginTop: 18 }}>
                <Link to="/products" className="button">{getHomeModuleText("intro", "primaryCtaText", String(pageTemplateConfig?.primaryCtaText || t("viewMore")).trim())}</Link>
                <Link to="/contact" className="button ghost">{getHomeModuleText("intro", "secondaryCtaText", String(pageTemplateConfig?.secondaryCtaText || t("contactUs")).trim())}</Link>
              </div>
            </div>
            <div className="loya-intro-trust">
              <div className="trust-strip trust-strip-inline">
                {TRUST_HIGHLIGHTS.map((item) => (
                  <article key={item.key} className="trust-strip-item">
                    <span className="trust-strip-icon" aria-hidden="true">{renderStorefrontIcon(item.iconImage)}</span>
                    <span className="trust-strip-label">{t(item.labelKey)}</span>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="seo-quick-nav home-module home-module-quick-nav" aria-label={t("homeAriaQuickNav") } style={getHomeModuleStyle("quick-nav")}>
          <header className="loya-section-head">
            <div>
              <h2>{getHomeModuleText("quick-nav", "title", t("homeQuickAccessTitle"))}</h2>
              {getHomeModuleText("quick-nav", "subtitle") ? <p>{getHomeModuleText("quick-nav", "subtitle")}</p> : null}
            </div>
          </header>
          <div className="seo-quick-nav-grid">
            {seoQuickLinks.map((item) => (
              <article key={item.title} className="seo-quick-nav-card">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
                <Link to={item.to} className="seo-inline-link">{getHomeModuleText("quick-nav", "primaryCtaText", t("viewMore"))}</Link>
              </article>
            ))}
          </div>
        </section>

        <section className="loya-categories-reference-section home-module home-module-categories" aria-label={t("homeAriaCategoryGallery") } style={getHomeModuleStyle("categories")}>
          <header className="loya-section-head loya-section-head-categories">
            <div>
              <h2>{getHomeModuleText("categories", "title", t("categoryTitle"))}</h2>
              {getHomeModuleText("categories", "subtitle") ? <p>{getHomeModuleText("categories", "subtitle")}</p> : null}
            </div>
          </header>
          <div className="loya-category-grid loya-category-grid-12 loya-category-grid-reference">
            {categoryShowcaseItems.length ? (
              categoryShowcaseItems.map((category) => (
                <Link key={category.key} to={category.path || "/products"} className="loya-category-card" aria-label={category.title}>
                  <img src={category.imageUrl || FALLBACK_ART} alt={category.title} loading="lazy" />
                  <div className="loya-category-overlay">
                    <strong>{category.title}</strong>
                  </div>
                </Link>
              ))
            ) : (!loading && !error ? renderEmptyProductsState(t("categoryTitle")) : null)}
          </div>
        </section>

        <section className="loya-best-selling-reference-section home-module home-module-best-selling" aria-label={t("homeAriaBestSellingGrid") } style={getHomeModuleStyle("best-selling")}>
          <header className="loya-section-head loya-section-head-best-selling" aria-label={t("homeAriaBestSellingHead") }>
            <div>
              <h2>{getHomeModuleText("best-selling", "title", t("homeBestSellingTitle"))}</h2>
              {getHomeModuleText("best-selling", "subtitle") ? <p>{getHomeModuleText("best-selling", "subtitle")}</p> : null}
            </div>
            <Link to="/contact" className="loya-best-selling-view-all">
              {getHomeModuleText("best-selling", "primaryCtaText", t("requestQuote"))} <span aria-hidden="true">-&gt;</span>
            </Link>
          </header>

          <div className="loya-product-grid loya-product-grid-best-selling loya-product-grid-best-selling-reference">
            {bestSellingProducts.length ? (
              bestSellingProducts.map((item) => (
                <article key={item.id} className="loya-product-card">
                  <Link to={item.path || "/products"} className="image-link" aria-label={item.name}>
                    <img src={item.imageUrl || FALLBACK_ART} alt={item.name} loading="lazy" />
                  </Link>
                  <div className="card-body">
                    <h3>
                      <Link
                        to={item.path || "/products"}
                        className="best-selling-title-link"
                        title={item.name}
                        aria-label={item.name}
                      >
                        {item.name}
                      </Link>
                    </h3>
                    <p className="loya-product-kicker">{t("homeBestSellingCardKicker")}</p>
                  </div>
                </article>
              ))
            ) : (!loading && !error ? renderEmptyProductsState(t("homeBestSellingTitle")) : null)}
          </div>
        </section>

        {loading ? <p className="state" style={{ order: 999 }}>{t("loading")}</p> : null}
        {error ? <p className="state error" style={{ order: 999 }}>{error}</p> : null}

        {!loading && !error ? (
          <>
            <section className="loya-section-head loya-section-head-featured home-module home-module-featured" id="section-products" style={getHomeModuleStyle("featured")}>
              <div>
                <h2>{getHomeModuleText("featured", "title", t("featuredTitle"))}</h2>
                {getHomeModuleText("featured", "subtitle") ? <p>{getHomeModuleText("featured", "subtitle")}</p> : null}
              </div>
              <Link to="/products" className="loya-featured-catalog-link">
                {getHomeModuleText("featured", "primaryCtaText", t("homeRequestCatalog"))} <span aria-hidden="true">-&gt;</span>
              </Link>
            </section>

            <section className="loya-product-grid loya-product-grid-featured home-module home-module-featured" style={getHomeModuleStyle("featured")}>
              {featuredProducts.length ? (
                featuredProducts.map((item) => {
                  const productImage = item.imageUrl || item.mainImages?.[0] || FALLBACK_ART;
                  const featuredTitle = getFeaturedCategoryTitle(item);
                  const featuredSubtitle = String(item.description || item.name || item.category || "").trim();
                  const categoryName = String(item.category || "").trim();
                  const groupId = parseProductGroupId(item);
                  const categoryPath = categoryShowcaseItems.find((c) => c.title === categoryName)?.path
                    || (groupId
                      ? (productGroupPathById.get(groupId) || `/products?groupId=${groupId}&group=${encodeURIComponent(categoryName)}`)
                      : (categoryName ? `/products?group=${encodeURIComponent(categoryName)}` : "/products"));

                  return (
                    <article key={item.id} className="loya-product-card">
                      <Link to={`/products/${item.id}`} className="image-link" aria-label={item.name}>
                        <img src={productImage} alt={item.name} loading="lazy" />
                      </Link>
                      <div className="card-body">
                        <h3 className="loya-featured-title">
                          <Link
                            to={categoryPath}
                            className="loya-featured-title-link"
                            title={categoryName || item.name}
                            aria-label={categoryName || item.name}
                          >
                            {featuredTitle}
                          </Link>
                        </h3>
                        <p className="loya-featured-subtitle">{featuredSubtitle}</p>
                      </div>
                    </article>
                  );
                })
              ) : renderEmptyProductsState(t("featuredTitle"))}
            </section>

            <section className="language-activity-panel seo-solution-guide home-module home-module-solutions" aria-label={t("homeAriaSolutionsNav") } style={getHomeModuleStyle("solutions")}>
              <div className="language-activity-head">
                <h2>{getHomeModuleText("solutions", "title", t("homeSolutionsTitle"))}</h2>
                {getHomeModuleText("solutions", "subtitle") ? <p>{getHomeModuleText("solutions", "subtitle")}</p> : null}
                <div className="seo-solution-guide-actions">
                  <Link className="button" to="/solutions">{getHomeModuleText("solutions", "primaryCtaText", t("homeSolutionsButton"))}</Link>
                </div>
              </div>
              <div className="language-activity-grid seo-solution-guide-grid">
                {buyerSegments.map((item) => (
                  <article key={item.label} className="language-activity-item seo-solution-guide-item">
                    <span className="language-flag" aria-hidden="true">
                      {item.iconImage ? renderStorefrontIcon(item.iconImage) : item.icon}
                    </span>
                    <span className="language-name">{item.label}</span>
                  </article>
                ))}
              </div>
            </section>

            <section className="seo-scenarios home-module home-module-scenarios" aria-label={t("homeAriaScenarios") } style={getHomeModuleStyle("scenarios")}>
              <header className="loya-section-head">
                <div>
                  <h2>{getHomeModuleText("scenarios", "title", t("homeScenariosTitle"))}</h2>
                  {getHomeModuleText("scenarios", "subtitle") ? <p>{getHomeModuleText("scenarios", "subtitle")}</p> : null}
                </div>
              </header>
              <div className="seo-scenarios-grid">
                {seoScenarios.map((item) => (
                  <article key={item.title} className="seo-scenario-card">
                    <figure className="seo-scenario-media">
                      <img src={item.image} alt={item.title} loading="lazy" />
                    </figure>
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                    <Link to={item.to} className="seo-inline-link">{getHomeModuleText("scenarios", "primaryCtaText", t("homeScenariosLink"))}</Link>
                  </article>
                ))}
              </div>
            </section>

            <section className="loya-image-text-stack loya-supply-strip-section home-module home-module-story" aria-label={t("homeAriaSupplyQuality") } style={getHomeModuleStyle("story")}>
              <div className="loya-supply-gallery-row" aria-label={t("homeShowcaseAria")}>
                <figure className="loya-supply-gallery-card loya-supply-gallery-card-main">
                  <img
                    src={getHomeModuleImage("story", "/assets/about/b2b-partner.png")}
                    alt={t("homeSupplyImageAlt")}
                    loading="lazy"
                  />
                </figure>
                {HOMEPAGE_SHOWCASE_IMAGES.map((item) => (
                  <figure key={item.key} className="loya-supply-gallery-card">
                    <img
                      src={item.src}
                      alt={t(item.altKey)}
                      loading="lazy"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = item.fallback;
                      }}
                    />
                  </figure>
                ))}
              </div>

              <div className="loya-supply-feature-strip">
                {serviceFeatures.map((item) => (
                  <article key={item.title} className="loya-supply-feature-item">
                    <span className="loya-supply-feature-icon" aria-hidden="true">
                      {item.iconImage ? renderStorefrontIcon(item.iconImage) : (item.icon || "•")}
                    </span>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.body}</p>
                    </div>
                  </article>
                ))}
              </div>

              <article id="solutions" className="loya-image-text-card loya-image-text-card-reviews">
              </article>

              <section className="trusted-markets-section home-module" aria-label={t("buyerReviewsAria")}>
                <header className="loya-section-head">
                  <div>
                    <h2>{t("homeQualitySupportTitle")}</h2>
                    <p>{t("homeQualitySupportBody")}</p>
                  </div>
                </header>
                <div className="trusted-markets-grid">
                  <article className="trusted-market-card">
                    <h3>{t("marketNorthAmerica")}</h3>
                    <p>{t("marketNorthAmericaNote")}</p>
                  </article>
                  <article className="trusted-market-card">
                    <h3>{t("marketEurope")}</h3>
                    <p>{t("marketEuropeNote")}</p>
                  </article>
                  <article className="trusted-market-card">
                    <h3>{t("marketAsiaPacific")}</h3>
                    <p>{t("marketAsiaPacificNote")}</p>
                  </article>
                  <article className="trusted-market-card">
                    <h3>{t("marketMiddleEast")}</h3>
                    <p>{t("marketMiddleEastNote")}</p>
                  </article>
                </div>
              </section>
            </section>

            <section className="seo-keyword-body" aria-label={t("homeAriaSourcingOverview") } hidden>
              <header className="loya-section-head">
                <div>
                  <h2>{t("homeSourcingInsightsTitle")}</h2>
                </div>
              </header>
              <article>
                <p>{seoKeywordBody}</p>
              </article>
            </section>

            <section className="seo-blog" aria-label={t("homeAriaBlog") } hidden>
              <header className="loya-section-head">
                <div>
                  <h2>{t("homeBlogTitle")}</h2>
                </div>
              </header>
              <div className="seo-blog-grid">
                {blogHighlights.map((item) => (
                  <article key={item.title} className="seo-blog-card">
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                    <Link to="/blog" className="seo-inline-link">{t("homeBlogReadMore")}</Link>
                  </article>
                ))}
              </div>
            </section>

          </>
        ) : null}

        <section className="loya-inquiry-cta-layout loya-inquiry-cta-showcase home-module home-module-inquiry">
          <article className="loya-inquiry-cta-copy">
            <p className="loya-inquiry-cta-eyebrow">{t("homeCtaEyebrow")}</p>
            <h2>{t("homeCtaTitle")}</h2>
            <p className="loya-inquiry-cta-description">
              {t("homeCtaDescription")}
            </p>
            <div className="loya-inquiry-cta-actions">
              <Link to="/contact" className="button loya-inquiry-cta-primary">{t("homeCtaPrimary")}</Link>
              <Link to="/products" className="button ghost loya-inquiry-cta-secondary">{t("homeCtaSecondary")}</Link>
            </div>
          </article>
          <aside className="loya-inquiry-cta-media" aria-hidden="true">
            <img
              src="https://images.unsplash.com/photo-1603988363607-e1e4a66962c6?auto=format&fit=crop&w=1300&q=86"
              alt=""
              loading="lazy"
            />
          </aside>
        </section>

      </main>

      <ProductSearchOverlay
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        apiBaseUrl={apiBaseUrl}
        privacyEnhanced={privacyEnhanced}
      />
    </>
  );
}
